
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Users, MessageSquare, CheckCircle, Clock, Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FeedbackRequest } from '@/components/feedback/types/dashboardTypes';

type DashboardTabProps = {
  isLoading: boolean;
  onForceSync: () => void;
};

type StatsType = {
  totalUsers: number;
  totalRequests: number;
  responseRate: number;
  activeRequests: number;
  recentActivity: number;
};

const DashboardTab: React.FC<DashboardTabProps> = ({ isLoading, onForceSync }) => {
  const [stats, setStats] = useState<StatsType>({
    totalUsers: 0,
    totalRequests: 0,
    responseRate: 0,
    activeRequests: 0,
    recentActivity: 0
  });

  const [contextDistribution, setContextDistribution] = useState<{name: string, value: number}[]>([]);
  const [weeklyActivity, setWeeklyActivity] = useState<{name: string, requests: number, responses: number}[]>([]);
  const [recentActivity, setRecentActivity] = useState<{id: number, type: string, description: string, time: string}[]>([]);
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // 1. Fetch total users count
      const { count: usersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      
      if (usersError) throw usersError;
      
      // 2. Fetch feedback requests stats
      const { data: requestsData, error: requestsError } = await supabase
        .from('feedback_requests')
        .select('status, created_at');
      
      if (requestsError) throw requestsError;
      
      // Calculate stats from the retrieved data
      const totalRequests = requestsData?.length || 0;
      const respondedRequests = requestsData?.filter(req => req.status === 'completed').length || 0;
      const responseRate = totalRequests > 0 ? Math.round((respondedRequests / totalRequests) * 100) : 0;
      const activeRequests = requestsData?.filter(req => req.status === 'pending').length || 0;
      
      // Calculate recent activity (last 7 days)
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const recentActivity = requestsData?.filter(req => 
        new Date(req.created_at) > oneWeekAgo
      ).length || 0;
      
      setStats({
        totalUsers: usersCount || 0,
        totalRequests,
        responseRate,
        activeRequests,
        recentActivity
      });
      
      // 3. Generate mock context distribution for the pie chart
      // Since we don't have a context field in the feedback_requests table,
      // we'll create sample data for now
      setContextDistribution([
        { name: 'Recrutement', value: 65 },
        { name: 'Freelance', value: 20 },
        { name: 'Évaluation 360°', value: 10 },
        { name: 'Autres', value: 5 }
      ]);
      
      // 4. Generate weekly activity data from actual data
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 6);
      
      const weeklyData = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(weekStart.getDate() + i);
        
        const dayName = day.toLocaleDateString('fr-FR', { weekday: 'short' }).substring(0, 3);
        const dayRequests = requestsData?.filter(req => {
          const reqDate = new Date(req.created_at);
          return reqDate.getDate() === day.getDate() && 
                reqDate.getMonth() === day.getMonth() && 
                reqDate.getFullYear() === day.getFullYear();
        }).length || 0;
        
        const dayResponses = requestsData?.filter(req => {
          const reqDate = new Date(req.created_at);
          return reqDate.getDate() === day.getDate() && 
                reqDate.getMonth() === day.getMonth() && 
                reqDate.getFullYear() === day.getFullYear() &&
                req.status === 'completed';
        }).length || 0;
        
        weeklyData.push({
          name: dayName,
          requests: dayRequests,
          responses: dayResponses
        });
      }
      
      setWeeklyActivity(weeklyData);
      
      // 5. Fetch most recent activities
      const { data: recentFeedbacks, error: recentError } = await supabase
        .from('feedbacks')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(3);
        
      const { data: recentRequests, error: recentReqError } = await supabase
        .from('feedback_requests')
        .select('id, created_at, status')
        .order('created_at', { ascending: false })
        .limit(3);
        
      // Combine recent activities
      const combinedActivities = [
        ...(recentFeedbacks || []).map((fb, idx) => ({
          id: idx,
          type: 'feedback',
          description: 'Feedback complété',
          time: formatTimeAgo(new Date(fb.created_at))
        })),
        ...(recentRequests || []).map((req, idx) => ({
          id: idx + 100, // Ensure unique IDs
          type: 'request',
          description: req.status === 'completed' 
            ? 'Feedback complété' 
            : 'Nouvelle demande de feedback',
          time: formatTimeAgo(new Date(req.created_at))
        }))
      ].sort((a, b) => {
        // Extract hours from "Il y a X heures"
        const hoursA = parseInt(a.time.match(/\d+/)?.[0] || '0');
        const hoursB = parseInt(b.time.match(/\d+/)?.[0] || '0');
        return hoursA - hoursB;
      }).slice(0, 5);
      
      setRecentActivity(combinedActivities);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      return "Il y a moins d'une heure";
    } else if (diffHours === 1) {
      return "Il y a 1 heure";
    } else {
      return `Il y a ${diffHours} heures`;
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
    onForceSync();
  };

  return (
    <Card>
      <CardHeader className="flex justify-between flex-col md:flex-row items-start md:items-center pb-2">
        <div>
          <CardTitle>Tableau de bord</CardTitle>
          <CardDescription>Vue synthétique de l'activité de la plateforme</CardDescription>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 mt-2 md:mt-0"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Rafraîchir les données
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-2">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <span className="text-3xl font-bold text-blue-600">{stats.totalUsers}</span>
                <span className="text-sm text-gray-500">Utilisateurs</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-2">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
                <span className="text-3xl font-bold text-purple-600">{stats.totalRequests}</span>
                <span className="text-sm text-gray-500">Demandes envoyées</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <span className="text-3xl font-bold text-green-600">{stats.responseRate}%</span>
                <span className="text-sm text-gray-500">Taux de réponse</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-2">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <span className="text-3xl font-bold text-amber-600">{stats.activeRequests}</span>
                <span className="text-sm text-gray-500">Demandes actives</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-2">
                  <Activity className="h-6 w-6 text-red-600" />
                </div>
                <span className="text-3xl font-bold text-red-600">{stats.recentActivity}</span>
                <span className="text-sm text-gray-500">Demandes cette semaine</span>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Activité hebdomadaire</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={weeklyActivity}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="requests" fill="#8884d8" name="Demandes" />
                    <Bar dataKey="responses" fill="#82ca9d" name="Réponses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Répartition par contexte</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={contextDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {contextDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Activité récente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start pb-3 border-b last:border-0 border-gray-100">
                    <div className={`w-8 h-8 rounded-full mr-3 flex items-center justify-center ${
                      activity.type === 'feedback' ? 'bg-green-100 text-green-600' : 
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {activity.type === 'feedback' ? 
                        <CheckCircle className="h-4 w-4" /> : 
                        <MessageSquare className="h-4 w-4" />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium">{activity.description}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Aucune activité récente
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};

export default DashboardTab;
